import { useParams } from "react-router-dom";

import { useMyInvoice } from "@/features/invoices/model/useInvoices";
import type { InvoiceStatus } from "@/features/invoices/types";
import { ROLE_LABELS } from "@/features/shifts/types";
import { BackLink, Badge, type BadgeProps } from "@/shared/ui";

const STATUS_VARIANT: Record<InvoiceStatus, BadgeProps["variant"]> = {
  GENERATED: "warning",
  SENT: "info",
  PAID: "success",
};

const STATUS_LABELS: Record<InvoiceStatus, string> = {
  GENERATED: "Draft",
  SENT: "Outstanding",
  PAID: "Paid",
};

function fmtLong(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function fmtShort(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}

function shortInvoiceId(id: string): string {
  return id.slice(0, 8).toUpperCase();
}

export function VenueInvoiceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, error } = useMyInvoice(id);

  return (
    <div className="space-y-4">
      <BackLink to="/venue/invoices" label="Back to invoices" />

      {isLoading && <div className="text-sm text-copy-muted">Loading invoice…</div>}
      {error && <div className="text-sm text-destructive">Could not load invoice.</div>}

      {data && (
        <div className="space-y-4 rounded-lg border border-border bg-surface p-6 shadow-card">
          <header className="flex flex-wrap items-start justify-between gap-3 border-b border-border pb-4">
            <div className="min-w-0">
              <h1 className="text-2xl font-semibold tracking-display-tight text-copy">
                Invoice
              </h1>
              <p className="mt-1 text-xs text-copy-subtle">
                #INV-{shortInvoiceId(data.id)}
                <span className="mx-2">·</span>
                Issued {fmtLong(data.created_at)}
              </p>
            </div>
            <div className="text-right">
              <Badge variant={STATUS_VARIANT[data.status]} dot>
                {STATUS_LABELS[data.status]}
              </Badge>
              {data.status === "PAID" && data.paid_at && (
                <p className="mt-1 text-xs text-copy-subtle">Paid {fmtShort(data.paid_at)}</p>
              )}
              {data.status === "SENT" && data.sent_at && (
                <p className="mt-1 text-xs text-copy-subtle">Sent {fmtShort(data.sent_at)}</p>
              )}
            </div>
          </header>

          <div className="grid grid-cols-1 gap-6 text-sm md:grid-cols-2">
            <div>
              <p className="text-xs uppercase tracking-wider text-copy-subtle">From</p>
              <p className="mt-1 font-semibold text-copy">OneShift</p>
              <p className="text-copy-muted">billing@oneshift.ca</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-copy-subtle">Bill to</p>
              <p className="mt-1 font-semibold text-copy">{data.venue_name}</p>
              <p className="text-copy-muted">{data.venue_address}</p>
            </div>
          </div>

          <div className="rounded-md bg-surface-sunken px-3 py-2 text-sm">
            <span className="text-copy-muted">Billing period: </span>
            <span className="font-medium text-copy">
              {fmtLong(data.period_start)} – {fmtLong(data.period_end)}
            </span>
          </div>

          <div>
            <p className="mb-2 text-xs font-medium uppercase tracking-wider text-copy-subtle">
              Line items
            </p>
            {data.line_items.length === 0 ? (
              <p className="rounded-md border border-dashed border-border p-4 text-center text-sm text-copy-muted">
                No shift line items.
              </p>
            ) : (
              <div className="overflow-hidden rounded-md border border-border">
                <table className="w-full text-sm">
                  <thead className="bg-surface-sunken text-left text-xs uppercase tracking-wider text-copy-muted">
                    <tr>
                      <th className="px-3 py-2 font-medium">Date</th>
                      <th className="px-3 py-2 font-medium">Role</th>
                      <th className="px-3 py-2 font-medium">Worker</th>
                      <th className="px-3 py-2 text-right font-medium">Hours</th>
                      <th className="px-3 py-2 text-right font-medium">Rate</th>
                      <th className="px-3 py-2 text-right font-medium">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {data.line_items.map((l) => (
                      <tr key={l.shift_id}>
                        <td className="px-3 py-2 text-copy">{fmtShort(l.date)}</td>
                        <td className="px-3 py-2 text-copy-muted">{ROLE_LABELS[l.role]}</td>
                        <td className="px-3 py-2 text-copy">{l.worker_name || "—"}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-copy">
                          {l.hours}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-copy">
                          ${l.pay_rate}
                        </td>
                        <td className="px-3 py-2 text-right font-medium tabular-nums text-copy">
                          ${l.amount}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="space-y-1 text-sm">
            <div className="flex justify-between py-1">
              <span className="text-copy-muted">Worker pay subtotal</span>
              <span className="tabular-nums text-copy">${data.total_worker_pay}</span>
            </div>
            <div className="flex justify-between py-1">
              <span className="text-copy-muted">
                OneShift fee ({data.markup_percentage}% markup)
              </span>
              <span className="tabular-nums text-copy">
                $
                {(
                  parseFloat(data.total_charge) - parseFloat(data.total_worker_pay)
                ).toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between border-t border-border pt-2 text-base font-semibold">
              <span className="text-copy">
                {data.status === "PAID" ? "Total" : "Total due"}
              </span>
              <span className="tabular-nums text-copy">${data.total_charge}</span>
            </div>
          </div>

          {data.status !== "PAID" && (
            <div className="rounded-md border border-info bg-info-light p-4 text-sm text-info-foreground">
              <p className="font-semibold">How to pay</p>
              <ul className="mt-2 list-disc space-y-1 pl-5">
                <li>
                  Send via Interac e-Transfer to{" "}
                  <span className="font-medium">billing@oneshift.ca</span>
                </li>
                <li>
                  Use reference{" "}
                  <span className="font-medium">INV-{shortInvoiceId(data.id)}</span> in the message
                </li>
                <li>Net 7 days from issue date</li>
              </ul>
              <p className="mt-2 text-xs text-info-foreground">
                Once received, OneShift marks the invoice paid and forwards worker pay. This page
                will update to "Paid" automatically.
              </p>
            </div>
          )}

          {data.status === "PAID" && (
            <div className="rounded-md border border-success bg-success-light p-3 text-sm text-success-foreground">
              <p className="font-semibold">Paid in full</p>
              <p className="mt-1 text-xs">
                Thanks — payment received {data.paid_at ? `on ${fmtLong(data.paid_at)}` : ""}.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
